import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from '@emotion/styled';
import { gridSize, colors } from '@atlaskit/theme';
import ButtonContainer from './ButtonContainer';
import TallButton from './TallButton';
import { SecurityWall } from './SecurityWall';
import { RecoveryEmailErrorReason } from '../types';

const ErrorMessage = styled.div`
  color: ${colors.R500};
  margin-bottom: ${gridSize() * 2}px;
`;

interface Props {
  email: string;
  sendEmail: () => void;
  recoveryEmailError: RecoveryEmailErrorReason | undefined;
}

const RecoveryEmailForm = ({ email, recoveryEmailError, sendEmail }: Props) => {
  const [loading, setLoading] = useState(false);

  const sendRecoveryEmail = () => {
    setLoading(true);
    sendEmail();
  };

  useEffect(() => {
    if (recoveryEmailError) {
      setLoading(false);
    }
  }, [recoveryEmailError]);

  return (
    <SecurityWall>
      <FormattedMessage
        id="mf.recoveryemailform.desc"
        tagName="p"
        defaultMessage="We'll email you at {email} with instructions for how to recover access to your account."
        values={{ email: <strong>{email}</strong> }}
      />
      <FormattedMessage
        id="mf.recoveryemailform.security.desc"
        tagName="p"
        defaultMessage="For security reasons, this process will take some time to complete, and you may be contacted by our Support team for verification."
      />
      <ButtonContainer>
        {recoveryEmailError && (
          <RecoveryEmailErrorMessage recoveryEmailError={recoveryEmailError} />
        )}
        <TallButton
          id="send-recovery-email-button"
          appearance="primary"
          isFullWidth
          onClick={sendRecoveryEmail}
          isDisabled={loading}
          isLoading={loading}>
          <FormattedMessage
            id="mf.recoveryemailform.sendemail"
            defaultMessage="Send recovery email"
          />
        </TallButton>
      </ButtonContainer>
    </SecurityWall>
  );
};

const RecoveryEmailErrorMessage = ({
  recoveryEmailError,
}: {
  recoveryEmailError: RecoveryEmailErrorReason;
}) => {
  if (!recoveryEmailError) {
    return null;
  }

  switch (recoveryEmailError) {
    case 'alreadyRequested':
      return (
        <ErrorMessage>
          <FormattedMessage
            id="mf.recoveryemailform.error.alreadyRequested"
            defaultMessage="You have already requested account recovery instructions. If the account exists, these instructions will be sent to your email address. Note that this may take some time to complete."
          />
        </ErrorMessage>
      );
    case 'userIsManaged':
    case 'userNotFound':
      return (
        <ErrorMessage>
          <FormattedMessage
            id="mf.recoveryemailform.error.genericExpected"
            defaultMessage="Your recovery request can't be processed. Please <support-link>contact support</support-link>."
            values={{
              'support-link': chunks => (
                <a href={'https://www.atlassian.com/company/contact/general-inquiries'}>{chunks}</a>
              ),
            }}
          />
        </ErrorMessage>
      );
    case 'unknown':
    default:
      return (
        <ErrorMessage>
          <FormattedMessage
            id="mf.recoveryemailform.error.unknown"
            defaultMessage="Unexpected error happened. Please <support-link>contact support</support-link>."
            values={{
              'support-link': chunks => (
                <a href={'https://www.atlassian.com/company/contact/general-inquiries'}>{chunks}</a>
              ),
            }}
          />
        </ErrorMessage>
      );
  }
};

export default RecoveryEmailForm;
